import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Partners from '../features/partners';
import Header from '../components/global/Header';
import WellnessHistory from '../features/wellness-history';
import Acheivements from '../features/awards';
import MyClasses from '../features/my-classes';
import MyPrograms from '../features/my-programs';
import Classes from '../features/classes';
import Videos from '../features/videos';
import Programs from '../features/programs';
import Nutrition from '../features/nutrition';
import PersonalTraining from '../features/personal-training';
import Psychology from '../features/psychology';
import HealthCoach from '../features/health-coach';
import Finances from '../features/finances';
import Membership from '../features/membership';
import FamilyPlan from '../features/family-plan';
import Account from '../features/account';
import ClassProfilePage from '../features/class-profile';
import PartnerProfilePage from '../features/partner-profile';
import ProgramsProfile from '../features/programs-profile';
import VideoProfile from '../features/video-profile';
import TermsAndConditionsPage from '../features/terms-and-conditions';
import { Loader } from '@beholos/holoscommon';
import { check, logout } from '../services/auth.service';
import { BASE_URL } from '../services/constants';
import Cancel from '../features/cancel';
import LoginMagic from '../features/login/LoginMagic';
import NotFound from '../features/notfound';
import { usePageTitle } from '../hooks/usePageTitle';

function PageWithTitle({ title, children }: { title: string; children: React.ReactNode }) {
  usePageTitle(title);
  return <>{children}</>;
}
import Dashboard from '../features/dashboard';
import LiveStreamRedirect from '../features/live-stream-redirect';
import DeleteUser from '../features/delete';
import UpdateMembership from '../features/update-membership';
import HeaderPartner from '../components/global/HeaderPartner';
import { Complaints } from '../features/complaints';
import usePageTracking from '../hooks/usePageTracking';
import { trackEvent } from '../services/analytics.service';
import YearInReview from '../features/year-in-review';

const PageLoading = () => (
  <div className="h-screen flex items-center justify-center">
    <Loader />
  </div>
);

function PageTracker() {
  usePageTracking();
  return null;
}

export default function AppRoutes() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPartnerUser, setIsPartnerUser] = useState(false);
  const isMagicLoginPath = typeof window !== 'undefined' && window.location.pathname.endsWith('/login/magic');
  const isCardifLoginPath = typeof window !== 'undefined' && window.location.pathname.endsWith('/login/cardif');
  const isPartnerProfilePath = typeof window !== 'undefined' && /^\/user\/partners\/[^/]+\/?$/.test(window.location.pathname);
  const isFamilyAcceptPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/familia/aceptar');

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        if (isMagicLoginPath || isCardifLoginPath || isFamilyAcceptPath) {
          return;
        }

        const data = await check();

        const isValidRedirect =
          data.redirectUrl === '/user/' ||
          (data.redirectUrl === '/partner/' && isPartnerProfilePath);
        setIsPartnerUser(data.redirectUrl === '/partner/' && isPartnerProfilePath);
        if (data.isAuthenticated && data.accessToken && isValidRedirect) {
          sessionStorage.setItem('token', data.accessToken);
          setIsAuthenticated(true);
          trackEvent('login_success');
        } else {
          sessionStorage.removeItem('token');
          setIsAuthenticated(false);
          await logout();
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        sessionStorage.removeItem('token');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  if (isLoading) {
    return <PageLoading />;
  }

  if (!isAuthenticated && !isMagicLoginPath && !isCardifLoginPath && !isFamilyAcceptPath) {
    window.location.href = `/iniciar-sesion?redirect=${encodeURIComponent(window.location.pathname)}`;
    return <PageLoading />;
  }

  const isMinimalLayout = isMagicLoginPath || isCardifLoginPath || isFamilyAcceptPath;

  return (
    <BrowserRouter basename={BASE_URL}>
      <PageTracker />
      {!isMinimalLayout && !isPartnerUser && <Header />}
      {isPartnerUser && <HeaderPartner />}
      {isMinimalLayout ? <Routes>
        <Route path="/login/magic" element={<LoginMagic />} />
      </Routes> : <Routes>
        {/* Static title for Prensa */}
        <Route path="/prensa" element={<PageWithTitle title="Prensa - Holos"><NotFound /></PageWithTitle>} />
        <Route path="/" element={<Dashboard />} />
        <Route path="/mi-cuenta" element={<Account />} />
        <Route path="/mi-cuenta/borrar" element={<DeleteUser />} />
        <Route path="/mi-historial" element={<WellnessHistory />} />
        <Route path="/mi-membresia" element={<Membership />} />
        <Route path="/mi-membresia/cancelar" element={<Cancel />} />
        <Route path="/mi-membresia/administrar" element={<UpdateMembership />} />
        <Route path="/plan-familiar" element={<FamilyPlan />} />
        <Route path="/mis-logros" element={<Acheivements />} />
        <Route path="/mis-clases" element={<MyClasses />} />
        <Route path="/unirme/:hash" element={<LiveStreamRedirect />} />
        <Route path="/mis-programas" element={<MyPrograms />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/partners/:partnerSlug" element={<PartnerProfilePage />} />
        <Route path="/clases" element={<Classes />} />
        <Route path="/clases/:partnerSlug/:classSlug/:timeStamp" element={<ClassProfilePage />} />
        <Route path="/clases-online" element={<Videos />} />
        <Route path="/clases-online/video/:slug" element={<VideoProfile />} />
        <Route path="/programas" element={<Programs />} />
        <Route path="/programas/:programSlug" element={<ProgramsProfile />} />
        <Route path="/resumen/:year" element={<YearInReview />} />

        <Route path="/nutricion" element={<Nutrition />} />
        <Route path="/nutricion/reservar" element={<Classes showNutritionist={true} />} />
        <Route path="/health-coach" element={<HealthCoach />} />
        <Route path="/health-coach/reservar" element={<Classes showHealthCoach={true} />} />
        <Route path="/finanzas" element={<Finances />} />
        <Route path="/finanzas/reservar" element={<Classes showFinancialAdvisor={true} />} />
        <Route path="/psicologia" element={<Psychology />} />
        <Route path="/psicologia/reservar" element={<Classes showPsychologist={true} />} />
        <Route path="/personal-training" element={<PersonalTraining />} />
        <Route path="/personal-training/reservar" element={<Classes showPersonalTrainer={true} />} />

        <Route path="/reclamos" element={<Complaints />} />
        <Route path="/terminos" element={<TermsAndConditionsPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>}
    </BrowserRouter>
  );
}
